import { FunctionComponent, useState } from 'react';
import { useIntl } from 'react-intl';

import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { Text } from '~/eds/src/components/Text/Text';
import { UserAction } from '~/eds/src/types/UserAction';
import { api } from '~/redux/api/createApi';

import { EsignatureIntegrationType, IntegrationMap } from '../../types';
import { IntegrationUserList } from '../components/IntegrationUserList';
import { useIntegrationsModalController } from './useIntegrationsModalController';

export interface ManageUserSideDrawerProps {
  disablePrimary?: boolean;
  integrationType: EsignatureIntegrationType;
  providerName: string;
  onRevokeClick?: () => void;
  onHide: () => void;
}
export const ManageUserSideDrawer: FunctionComponent<
  ManageUserSideDrawerProps
> = ({
  disablePrimary,
  integrationType,
  providerName = '',
  onRevokeClick = () => {},
  onHide = () => {},
}) => {
  const { activeModal } = useIntegrationsModalController();
  const { data: integrationUsers = [], isLoading: isLoadingIntegrationUsers } =
    api.endpoints.getEsignatureIntegrationUsers.useQuery(
      {
        integrationId: activeModal?.integrationId ?? '',
      },
      { skip: !activeModal },
    );
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const intl = useIntl();

  const footer: { actions: UserAction[] } = {
    actions: [
      {
        text: intl.formatMessage({
          id: 'ESignatureIntegrationCard.cancel',
          defaultMessage: 'Cancel',
          description: 'Cancel button text',
        }),
        level: 'tertiary',
        onClick: onHide,
      },
      {
        disabled: disablePrimary,
        text: intl.formatMessage({
          id: 'FieldAssignmentMenuContent.revokeButton',
          defaultMessage: 'Revoke',
          description: 'Revoke Integration Users button text',
        }),
        level: 'primary',
        onClick: onRevokeClick,
      },
    ],
  };

  return (
    <Panel
      actions={[
        {
          icon: 'x',
          text: intl.formatMessage({
            id: 'AttemptDetails.closeAction',
            defaultMessage: 'Close',
            description: 'Close button text',
          }),
          onClick: onHide,
        },
      ]}
      enableBackdrop
      footer={footer}
      position="right"
      width="600px"
      hidden={{
        isHidden: true,
        onHide: onHide,
        placement: 'right',
      }}
      title={intl.formatMessage({
        id: 'NewESignature.SidePanel.Title',
        defaultMessage: 'Manage authenticated users',
        description:
          'Title of the side panel for managing users in the selected e-signature integration',
      })}
    >
      <Text preset="description" textAlign="left">
        {integrationType === IntegrationMap.company
          ? intl.formatMessage({
              id: 'NewESignature.SidePanel.Description.Company',
              defaultMessage:
                'Revoke a user to reset their access to the integration. The user may regain access by authenticating with the integration again.',
              description:
                'Description of the side panel for managing users in the selected e-signature integration in Company mode',
            })
          : intl.formatMessage({
              id: 'NewESignature.SidePanel.Description.Personal',
              defaultMessage:
                'Select all the users to revoke and reset their access to the integration. The user may regain access by authenticating with the integration again.',
              description:
                'Description of the side panel for managing users in the selected e-signature integration in Personal mode',
            })}
      </Text>
      <Layout pt={3} pb={4} direction="column" spacing={3}>
        {integrationType === IntegrationMap.company && (
          <StatusMessage
            status="info"
            message={intl.formatMessage(
              {
                id: 'NewESignature.CompanyUserList.Information',
                defaultMessage:
                  'Signers will receive emails from this authenticated account. Revoking this account will delete the {providerName} integration and remove access for all users.',
                description: 'Information text for company user list',
              },
              { providerName },
            )}
          />
        )}
        {activeModal?.integrationType === IntegrationMap.personal && (
          <SearchInput
            placeholder={intl.formatMessage({
              id: 'NewESignature.SearchInput.Placeholder',
              defaultMessage: 'Search by name or email ...',
              description: 'Placeholder text for the search input field',
            })}
            name="search users"
            width="100%"
            onChange={(searchKeyword) =>
              setSearchKeyword(String(searchKeyword))
            }
            value={searchKeyword}
          />
        )}
      </Layout>
      <IntegrationUserList
        data={integrationUsers}
        isLoadingIntegrationUsers={isLoadingIntegrationUsers}
        searchKeyword={searchKeyword}
      />
    </Panel>
  );
};
